import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { makeRequest } from 'Douleutaras/services/apiClient';
import { CATEGORIES_MENU_CACHE_TTL_SECONDS, LANGUAGE_CODES, LOCAL_STORAGE_KEY } from 'Douleutaras/constants/system';
import { CategoryMenu } from 'Douleutaras/types/index';
import localStorage from 'Douleutaras/app/lib/storageWrapper';
import { getNow } from 'Douleutaras/utilities/localStorage';

interface CategoriesMenuCacheEntry {
  language: string;
  data: Array<CategoryMenu>;
  cachedAt: number;
}

// Fallback for pages that never go through SSR (most of the app — only the
// anonymized pro-profile route and sign-in are server-rendered, see
// src/server/index.tsx's route mounts) and so never get categoriesMenu
// seeded into the store for free. Not consulted at all when SSR (or a
// previous fetch this session) already provided the current language's data
// — see the early-return in DefaultHeader.tsx's effect.
export const getCachedCategoriesMenu = (language: string): Array<CategoryMenu> | undefined => {
  const storage = localStorage();
  const cached: CategoriesMenuCacheEntry = storage.get(LOCAL_STORAGE_KEY.CATEGORIES_MENU);
  if (!cached || cached.language !== language) {
    return undefined;
  }

  const isExpired = getNow() - cached.cachedAt > CATEGORIES_MENU_CACHE_TTL_SECONDS;

  return isExpired ? undefined : cached.data;
};

const setCachedCategoriesMenu = (language: string, data: Array<CategoryMenu>): void => {
  const entry: CategoriesMenuCacheEntry = { language, data, cachedAt: getNow() };
  const storage = localStorage();
  storage.set(LOCAL_STORAGE_KEY.CATEGORIES_MENU, entry);
};

export const fetchCategoriesMenu = createAsyncThunk(
  'categoriesMenu/fetchCategoriesMenu',
  async (language: string, api) => {
    const endpoint = `api/public/category_megamenu/?alias=bsdir_page_menu${language === LANGUAGE_CODES.EN ? '_en' : ''}`;

    const additionalRequestOptions = {
      additionalHeaders: undefined
    };

    // If language is defined, send a languagecode header which saves values to different language
    if (language) {
      additionalRequestOptions.additionalHeaders = {
        languagecode: language
      };
    }

    const response = await makeRequest(endpoint,
      {
        method: 'GET',
        ...additionalRequestOptions
      }
    )(api);

    setCachedCategoriesMenu(language, response.data);

    return response.data;
  }
);

interface HydrateCategoriesMenuFromCachePayload {
  data: Array<CategoryMenu>;
  language: string;
}

export const categoriesMenuSlice = createSlice({
  name: 'categoriesMenu',
  initialState: {
    data: [] as Array<CategoryMenu>,
    language: undefined as string | undefined,
    loading: false,
    error: undefined
  },
  reducers: {
    // Used to seed state from the server-side categories-menu cache (see
    // src/server/lib/categoriesMenuCache.ts) without going through the
    // fetchCategoriesMenu thunk's pending/fulfilled lifecycle.
    hydrateCategoriesMenuFromCache: (state, action: PayloadAction<HydrateCategoriesMenuFromCachePayload>) => {
      state.data = action.payload.data;
      state.language = action.payload.language;
      state.loading = false;
      state.error = undefined;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCategoriesMenu.pending, state => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchCategoriesMenu.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.language = action.meta.arg;
      })
      .addCase(fetchCategoriesMenu.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { hydrateCategoriesMenuFromCache } = categoriesMenuSlice.actions;
